import React, { useEffect, useRef } from 'react';
import { Placeholder } from '@sitecore-jss/sitecore-jss-react';
import { Section, Container } from 'components/shared';
import styled from 'styled-components';
import { useSitecoreContext } from 'stores/sitecoreContext';
import pageTypes from 'utils/pageTypes';
import { useLocation } from 'react-router-dom';
import { scrollToSection } from 'utils/scrollTo';
import cx from 'classnames';
import SalesPerson from 'components/containers/SalesPerson';
import SalesCenterContact from 'components/containers/SalesCenterContact';
import breakpoints from 'utils/breakpoints';

const StyledSection = styled(Section)`
  ${({ withoutMargin }) =>
    withoutMargin
      ? `padding-top: 2.25rem; 
         padding-bottom: 2.25rem; 
         @media ${breakpoints.lg} {
            padding-top: 0rem; 
            padding-bottom: 0rem;
      }`
      : `padding-bottom: 4.875rem; 
         margin-bottom: 1.875rem; 
      `}
`;

const hasSection = (rendering, placeholder, field) => {
  try {
    return !!rendering.placeholders[placeholder][0].fields[field].value;
  } catch (err) {
    return false;
  }
};

const hasContactSection = (rendering) => {
  return (
    hasSection(rendering, 'row1xcolumn2andcolumn3', 'phoneNumberCall') ||
    hasSection(rendering, 'row1xcolumn2andcolumn3', 'zipcode') ||
    hasSection(rendering, 'row1xcolumn2andcolumn3', 'firstAddress') ||
    hasSection(rendering, 'row2xcolumn2', 'email') ||
    hasSection(rendering, 'row2xcolumn3', 'email')
  );
};
const hasSecondContactSection = (rendering) => {
  return (
    hasSection(rendering, 'row1xcolumn2andcolumn3', 'secondSalesPerson') ||
    hasSection(rendering, 'row2xcolumn2', 'secondSalesPerson')
  );
};

const GetInTouchContainer = ({ rendering }) => {
  const { templateId = '' } = useSitecoreContext() || {};
  const currentPageType =
    rendering.placeholders.row1androw2xcolumn1[0] &&
    rendering.placeholders.row1androw2xcolumn1[0].fields &&
    rendering.placeholders.row1androw2xcolumn1[0].fields.currentPageType &&
    rendering.placeholders.row1androw2xcolumn1[0].fields.currentPageType.value;

  const location = useLocation();
  const hash = location.hash;
  useEffect(() => {
    if (hash && (hash === 'warrantyrequest' || hash === 'contact')) {
      setTimeout(() => {
        scrollToSection(hash.substring(1), 80);
      }, 1000);
    }
  }, [hash]);

  useEffect(() => {
    setTimeout(() => {
      console.log('trying to load FormCapture script on getIntouchContainer');
      let formCaptureScript = document.createElement('script');
      formCaptureScript.src =
        'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/can/FormCapture/FormCapture.bundle.js';
      formCaptureScript.onload = () => {
        console.log('getIntouchContainer FormCapture script loaded');
      };
      formCaptureScript.onerror = (e) => {
        console.log('getIntouchContainer FormCapture script error', e);
      };
      document.head.appendChild(formCaptureScript);
    }, 1000);
  }, []);
  const dynamicsFormFields =
    rendering.placeholders['column-form'][0] &&
    rendering.placeholders['column-form'][0].fields &&
    rendering.placeholders['column-form'][0].fields.dynamicsFormFields &&
    rendering.placeholders['column-form'][0].fields.dynamicsFormFields.value;
  const currentPageTypeForm =
    rendering.placeholders['column-form'][0] &&
    rendering.placeholders['column-form'][0].fields &&
    rendering.placeholders['column-form'][0].fields.currentPageType &&
    rendering.placeholders['column-form'][0].fields.currentPageType.value;
  const signUpForUpdatesStyle = true;
  return (
    <>
      <StyledSection
        className={cx(' ', {
          'bg-light-blue ': !signUpForUpdatesStyle,
          'mt-6 md:mt-16': currentPageType === pageTypes.MpcPage,
        })}
        withoutMargin={
          currentPageType === pageTypes.CondoPlan ||
          currentPageType === pageTypes.MpcPage ||
          currentPageType === pageTypes.CommunityPage ||
          currentPageType === pageTypes.CondoPage ||
          currentPageType === pageTypes.QmiPage ||
          currentPageType === pageTypes.PlanPage
        }
        id={'contact'}
        tabIndex={-1}
        noPaddingMobile
      >
        <Container id={'sales-center-contact'} tabIndex='-1' className='w-full'>
          <div className='px-0 lg:px-16 h-full flex flex-col justify-center overflow-visible'>
            <div className='flex w-full'>
              <Placeholder name='row1androw2xcolumn1' rendering={rendering} />
            </div>
            {currentPageType === pageTypes.MarketPage ? (
              <div className='flex flex-col w-full mb-10 md:mb-0 items-center'>
                <Placeholder
                  name='column-form'
                  rendering={rendering}
                  enableDynaMXFormAPI={true}
                  hasNoSectionsLeft={!hasContactSection(rendering)}
                  dynamicsFormFields={dynamicsFormFields}
                  currentPageType={currentPageTypeForm}
                  signUpForUpdatesStyle={signUpForUpdatesStyle}
                />
              </div>
            ) : (
              <div className='flex flex-col md:flex-row w-full mb-0'>
                {hasContactSection(rendering) && (
                  <div className='flex flex-col w-full lg:w-5/12 mb-10 md:mb-0 md:mr-10'>
                    {(hasSection(
                      rendering,
                      'row1xcolumn2andcolumn3',
                      'phoneNumberCall',
                    ) ||
                      hasSection(
                        rendering,
                        'row1xcolumn2andcolumn3',
                        'zipcode',
                      ) ||
                      hasSection(
                        rendering,
                        'row1xcolumn2andcolumn3',
                        'firstAddress',
                      )) && (
                      <SalesCenterContact
                        fields={
                          rendering.placeholders.row1xcolumn2andcolumn3[0]
                            .fields
                        }
                      />
                    )}
                    {hasSection(rendering, 'row2xcolumn2', 'email') && (
                      <div className='flex w-full mb-10 md:mb-12'>
                        <SalesPerson
                          fields={rendering.placeholders.row2xcolumn2[0].fields}
                        />
                      </div>
                    )}
                    {hasSecondContactSection && (
                      <SalesCenterContact
                        fields={
                          rendering.placeholders.row1xcolumn2andcolumn3[0]
                            .fields.secondSalesCenter
                        }
                      />
                    )}
                    {hasSecondContactSection && (
                      <div className='flex w-full mb-10 md:mb-12'>
                        <SalesPerson
                          fields={
                            rendering.placeholders.row2xcolumn2[0].fields
                              .secondSalesPerson
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

                <div
                  className={cx(
                    'flex flex-col w-full justify-between flex-wrap ',
                    {
                      'lg:pl-4 lg:w-7/12 ': hasContactSection(rendering),
                    },
                  )}
                >
                  <div className='flex flex-col w-full items-center justify-center pb-4 md:pb-8'>
                    <Placeholder
                      name='column-form'
                      rendering={rendering}
                      enableDynaMXFormAPI={true}
                      hasNoSectionsLeft={!hasContactSection(rendering)}
                      dynamicsFormFields={dynamicsFormFields}
                      currentPageType={currentPageTypeForm}
                      signUpForUpdatesStyle={signUpForUpdatesStyle}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </StyledSection>
    </>
  );
};
export default GetInTouchContainer;
